import { useState } from "react";
import { toast } from "sonner";
import { Mic2, HandCoins, Store, HeartHandshake, Newspaper, Handshake, Phone, Mail, MapPin, ArrowRight, Send, RotateCcw } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import Seo from "@/components/site/Seo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { submitContact } from "@/lib/api";
import { BRAND, INQUIRY_TYPES } from "@/lib/constants";

const PATHS = [
  { icon: Mic2, label: "Contestant", value: "Contestant" },
  { icon: HandCoins, label: "Sponsor", value: "Sponsor" },
  { icon: Store, label: "Vendor", value: "Vendor" },
  { icon: HeartHandshake, label: "Volunteer", value: "Volunteer" },
  { icon: Newspaper, label: "Media", value: "Media" },
  { icon: Handshake, label: "Partner", value: "Partnership" },
];

const initial = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  organization: "",
  inquiry_type: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const setInquiry = (v) => {
    setForm((f) => ({ ...f, inquiry_type: v }));
    const el = document.getElementById("contact-form");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.inquiry_type) {
      toast.error("Please select an inquiry type.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact(form);
      setSubmitted(true);
      toast.success("Thank you. Your message has been received.");
      setForm(initial);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo />
      <PageHero
        pageTitle="Contact"
        eyebrowNumber="01"
        eyebrow="Contact The Team"
        title="Reach"
        highlight="Us Directly."
        tagline="One inbox. One phone. One producer team that responds personally."
        description="Choose your path below or jump straight to the form."
      />

      {/* SIX PATHS */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="02">Six Contact Paths</SectionLabel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {PATHS.map((p) => {
              const Icon = p.icon;
              return (
                <button
                  key={p.value}
                  onClick={() => setInquiry(p.value)}
                  data-testid={`contact-path-${p.value.toLowerCase()}`}
                  className="card-dark p-8 text-left group"
                >
                  <Icon className="text-gold" size={32} strokeWidth={1.4} />
                  <h3 className="font-shoulders font-bold uppercase tracking-wider text-2xl text-white mt-4">
                    {p.label}
                  </h3>
                  <p className="font-manrope text-soft-gray text-sm mt-2">
                    Jump to the contact form, prefilled for {p.label.toLowerCase()} inquiries.
                  </p>
                  <div className="mt-5 flex items-center gap-2 text-gold font-bebas tracking-[0.22em] text-sm group-hover:text-gold-bright transition">
                    Open Form <ArrowRight size={14} />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* INFO + FORM */}
      <section
        id="contact-form"
        className="bg-navy border-t border-slate-soft"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10">
          {/* INFO */}
          <div className="lg:col-span-5">
            <SectionLabel number="03">Direct Lines</SectionLabel>
            <div className="corner-bracket card-dark p-10 space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="text-gold mt-1" size={22} />
                <div>
                  <div className="label-bebas">Email</div>
                  <a
                    href={`mailto:${BRAND.email}`}
                    data-testid="contact-email-link"
                    className="font-shoulders font-bold uppercase tracking-wide text-xl text-white hover:text-gold transition break-all"
                  >
                    {BRAND.email}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="text-gold mt-1" size={22} />
                <div>
                  <div className="label-bebas">Phone</div>
                  <a
                    href={`tel:${BRAND.phoneTel}`}
                    data-testid="contact-phone-link"
                    className="font-shoulders font-bold uppercase tracking-wide text-xl text-white hover:text-gold transition"
                  >
                    {BRAND.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="text-gold mt-1" size={22} />
                <div>
                  <div className="label-bebas">Producer</div>
                  <p className="font-shoulders font-bold uppercase tracking-wide text-xl text-white">
                    {BRAND.producer}
                  </p>
                  <p className="font-manrope text-muted-ink text-sm mt-1">
                    {BRAND.city}
                  </p>
                  <a
                    href={BRAND.parentSiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-manrope text-gold hover:text-gold-bright text-sm transition"
                  >
                    {BRAND.parentSite}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <SectionLabel number="04">Send A Message</SectionLabel>
            {submitted ? (
              <div
                data-testid="contact-thank-you"
                className="corner-bracket card-dark p-10 text-center"
              >
                <div className="label-bebas text-gold">Message Received</div>
                <h3 className="font-bebas text-white text-3xl mt-2">
                  Thank You.
                </h3>
                <p className="font-manrope text-soft-gray mt-4 max-w-md mx-auto">
                  A member of the producer team will respond personally.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  data-testid="contact-submit-another"
                  className="btn-outline mt-8"
                >
                  <RotateCcw size={18} strokeWidth={1.8} />
                  Send Another
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="card-dark p-8 sm:p-10 space-y-6"
                noValidate
              >
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label className="label-bebas" htmlFor="ct-fn">First Name *</label>
                    <input
                      id="ct-fn"
                      name="first_name"
                      required
                      value={form.first_name}
                      onChange={onChange}
                      data-testid="contact-input-first-name"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="label-bebas" htmlFor="ct-ln">Last Name *</label>
                    <input
                      id="ct-ln"
                      name="last_name"
                      required
                      value={form.last_name}
                      onChange={onChange}
                      data-testid="contact-input-last-name"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="label-bebas" htmlFor="ct-em">Email *</label>
                    <input
                      id="ct-em"
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={onChange}
                      data-testid="contact-input-email"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="label-bebas" htmlFor="ct-ph">Phone</label>
                    <input
                      id="ct-ph"
                      name="phone"
                      value={form.phone}
                      onChange={onChange}
                      data-testid="contact-input-phone"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="label-bebas" htmlFor="ct-org">Organization</label>
                    <input
                      id="ct-org"
                      name="organization"
                      value={form.organization}
                      onChange={onChange}
                      data-testid="contact-input-organization"
                      className="input-dark"
                    />
                  </div>
                  <div>
                    <label className="label-bebas">Inquiry Type *</label>
                    <Select
                      value={form.inquiry_type}
                      onValueChange={(v) =>
                        setForm((f) => ({ ...f, inquiry_type: v }))
                      }
                    >
                      <SelectTrigger
                        data-testid="contact-select-inquiry"
                        className="input-dark rounded-none h-[52px] text-left"
                      >
                        <SelectValue placeholder="Choose one" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-card border-slate-soft text-white">
                        {INQUIRY_TYPES.map((it) => (
                          <SelectItem key={it} value={it}>
                            {it}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="label-bebas" htmlFor="ct-msg">Message *</label>
                  <textarea
                    id="ct-msg"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    data-testid="contact-input-message"
                    className="input-dark"
                    placeholder="Tell us what you want to discuss."
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="btn-gold w-full sm:w-auto"
                >
                  <Send size={18} strokeWidth={1.8} />
                  {submitting ? "Sending…" : "Send Message"}
                  {!submitting && <ArrowRight size={16} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
